/**
 * Typed API Clients for Platform Admin
 *
 * Singleton openapi-fetch client instances configured for the Vite dev server
 * proxy paths. Each client enforces compile-time type safety for request
 * parameters and response shapes via the generated OpenAPI path types.
 *
 * Base URLs correspond to Vite proxy rewrites:
 *   /api/admin/*  -> platform-admin-api (port 9302)
 *   /api/platform/* -> platform-admin-api auth routes
 */

import {
  createPlatformAdminClient,
  createAuditTrailClient,
  createPropertyTitleAdminClient,
  createRegulatoryReadinessClient,
  createUccFilingLifecycleClient,
  createUccEntitiesClient,
  createUccJurisdictionRulesClient,
  createUccFilingSubmissionClient,
  createUccPaperFilingClient,
  createUccSearchClient,
  createUccFeeReconciliationClient,
  createUccFilingAnalyticsClient,
  createUccBulkOperationsClient,
  createUccFilingAuditComplianceClient,
  createUccMultiStateFilingClient,
  createUccFilingWebhooksClient,
  createLoanSyndicationClient,
  createCooperativeReserveTokenClient,
  createIdentityProviderClient,
  createTypedClient,
} from '@aaimplatform/institutional-types/api-clients';
import type { paths as SupportPlatformPaths } from '@aaimplatform/institutional-types/support-platform';
import type { paths as DataBrowserPaths } from '@aaimplatform/institutional-types/admin-data-browser';
import type { paths as ConsumerAnonymousOnboardingPaths } from '@aaimplatform/institutional-types/generated/consumer-anonymous-onboarding';

// ---------------------------------------------------------------------------
// Platform Admin API -- tenant, integration, aaim-services, health, settings
// Spec paths: /auth/login, /tenants, /integrations, /integrations/third-party, /integrations/aaim-services, /health, /settings
// Vite proxy: /api/admin -> port 9302
// ---------------------------------------------------------------------------

export const platformAdminClient = createPlatformAdminClient('/api/admin');

// ---------------------------------------------------------------------------
// Platform Admin API (root) -- for spec paths that already include /admin/ prefix
// (e.g. /admin/provider-credentials/*). Base /api avoids double /admin/admin.
// ---------------------------------------------------------------------------

export const platformAdminRootClient = createPlatformAdminClient('/api');

// ---------------------------------------------------------------------------
// Platform Auth -- login endpoint shares the same backend but uses /api/platform prefix
// Spec path: /auth/login
// Vite proxy: /api/platform -> port 9302
// ---------------------------------------------------------------------------

export const platformAuthClient = createPlatformAdminClient('/api/platform');

// ---------------------------------------------------------------------------
// Audit Trail API -- audit log CRUD and integrity verification
// Spec paths: /audit/logs, /audit/logs/{logId}, /audit/logs/{logId}/verify
// Vite proxy: /api/admin -> port 9302 (audit routes share the admin API)
// ---------------------------------------------------------------------------

export const auditTrailClient = createAuditTrailClient('/api/admin');

// ---------------------------------------------------------------------------
// Property & Title Admin API -- property valuations, title searches, ATTOM testing
// Spec paths: /property/*, /title/*, /property/providers/attom/*
// Vite proxy: /api -> port 9302 (shares the admin API backend)
// ---------------------------------------------------------------------------

export const propertyTitleAdminClient = createPropertyTitleAdminClient('/api/v1');

// ---------------------------------------------------------------------------
// Regulatory Readiness Engine (port 9395 via proxy)
// Spec paths: /api/v1/regulatory/heat-map, /governing-law, /jurisdictions, /reports
// Vite proxy: /api/v1/regulatory -> port 9395
// ---------------------------------------------------------------------------

export const regulatoryReadinessClient = createRegulatoryReadinessClient('');

// ---------------------------------------------------------------------------
// UCC Filing Service (aaim-core ucc-filing, port 9554)
// Covers: filing lifecycle, entities, analytics, bulk ops, multi-state, audit,
// FI webhooks -- ONE express.Router grouping all of these under /api/v1/ucc-filing.
// Canonical baseUrl /api/v1/ucc-filing: the LB routes /api/v1/ucc-filing/* DIRECTLY
// to the deployed ucc-filing Cloud Run service on BOTH admin.aaim.com and
// platform.aaim.com (no SPA-nginx rewrite -- api-route-layer-consistency.md Trap A).
// Migrated 2026-06-20 off the non-canonical /api/ucc-lifecycle nginx-strip prefix
// (which proxied to an ingress-locked upstream -> 404). Mirrors admin-portal.
// ---------------------------------------------------------------------------

export const uccFilingLifecycleClient = createUccFilingLifecycleClient('/api/v1/ucc-filing');
export const uccEntitiesClient = createUccEntitiesClient('/api/v1/ucc-filing');
export const uccFilingAnalyticsClient = createUccFilingAnalyticsClient('/api/v1/ucc-filing');
export const uccBulkOperationsClient = createUccBulkOperationsClient('/api/v1/ucc-filing');
export const uccFilingAuditComplianceClient = createUccFilingAuditComplianceClient('/api/v1/ucc-filing');
export const uccMultiStateFilingClient = createUccMultiStateFilingClient('/api/v1/ucc-filing');
export const uccFilingWebhooksClient = createUccFilingWebhooksClient('/api/v1/ucc-filing');

// ---------------------------------------------------------------------------
// UCC Jurisdiction Rules Service (aaim-core jurisdiction-rules, port 9553)
// Canonical baseUrl /api/v1/ucc-jurisdiction -> LB routes directly to the
// deployed jurisdiction-rules Cloud Run service (mirrors admin-portal).
// ---------------------------------------------------------------------------

export const uccJurisdictionRulesClient = createUccJurisdictionRulesClient('/api/v1/ucc-jurisdiction');

// ---------------------------------------------------------------------------
// UCC Filing Submission Service (aaim-core filing-submission, port 9555)
// NOT YET DEPLOYED: the filing-submission service is container-buildable but has
// no Cloud Run revision, no LB backend/NEG/path rule, and mounts its routers at
// ROOT (no /api/v1/ucc-submission prefix). baseUrl stays non-canonical until the
// backend is deployed + remounted canonically (tracked: GH #1733 follow-on
// "UCC submission + search backend deploy"). Canonicalizing the baseUrl now would
// only relabel a 404 -- the backend genuinely does not exist on either portal.
// ---------------------------------------------------------------------------

export const uccFilingSubmissionClient = createUccFilingSubmissionClient('/api/ucc-submission');
export const uccPaperFilingClient = createUccPaperFilingClient('/api/ucc-submission');

// ---------------------------------------------------------------------------
// UCC Search Service (aaim-core ucc-search, port 9556)
// NOT YET DEPLOYED (same status as filing-submission above): no Cloud Run
// revision, no LB rule, root-mounted. baseUrl stays non-canonical until the
// backend deploys (tracked: GH #1733 follow-on).
// ---------------------------------------------------------------------------

export const uccSearchClient = createUccSearchClient('/api/ucc-search');

// ---------------------------------------------------------------------------
// UCC Fee Reconciliation Service (aaim-core ucc-fee-reconciliation, port 9557)
// Canonical baseUrl /api/v1/ucc-fees -> LB routes directly to the deployed
// ucc-fee-reconciliation Cloud Run service (mirrors admin-portal). Migrated
// 2026-06-20 off the non-canonical /api/ucc-fees nginx-strip prefix.
// ---------------------------------------------------------------------------

export const uccFeeReconciliationClient = createUccFeeReconciliationClient('/api/v1/ucc-fees');

// ---------------------------------------------------------------------------
// Loan Syndication Protocol Service (port 9570)
// Spec paths: /api/v1/syndication/offerings, /positions, /commitments, etc.
// Vite proxy: /api/v1/syndication -> port 9570 (no rewrite)
// nginx: /api/v1/syndication/ -> host.docker.internal:9570 (no rewrite)
//
// baseUrl is empty: the spec already declares the full /api/v1/syndication/*
// prefix, matching the canonical regulatoryReadinessClient pattern (line 79).
// Sprint C13 WP-5: corrected from '/api/syndication' to '' to eliminate path
// doubling (/api/syndication/api/v1/syndication/...).
// ---------------------------------------------------------------------------

export const loanSyndicationClient = createLoanSyndicationClient('');

// ---------------------------------------------------------------------------
// Cooperative Reserve Token Service (port 9571)
// Spec paths: /api/v1/crt/mint, /burn, /transfer, /positions, /reserve, /network
// Vite proxy: /api/v1/crt -> port 9571 (no rewrite)
// nginx: /api/v1/crt/ -> host.docker.internal:9571 (no rewrite)
//
// baseUrl is empty: same canonical pattern as regulatoryReadinessClient and
// loanSyndicationClient above. Sprint C13 WP-5: corrected from '/api/crt' to
// '' to eliminate path doubling (/api/crt/api/v1/crt/...).
// ---------------------------------------------------------------------------

export const cooperativeReserveTokenClient = createCooperativeReserveTokenClient('');

// ---------------------------------------------------------------------------
// Support Platform Service (aaim-core, port 9564)
// Spec paths: /api/v1/support/cases, /fi-cases, /agents, /sla, /aaim-e-handoff
// Vite proxy: /api/v1/support -> port 9564 (no rewrite)
// nginx: /api/v1/support/ -> ${SUPPORT_URL} (aaim-core-support:9564, no rewrite)
//
// baseUrl is empty: spec paths already carry the full /api/v1/support/* prefix,
// same canonical pattern as loanSyndicationClient / cooperativeReserveTokenClient.
// This SPA (platform.aaim.com, Aaim-operator) consumes the platform-admin
// audience (GET /cases across all tenants); the FI-operator audience lives in
// admin-portal. authMiddleware() (client.ts) attaches the CSRF header on
// mutations and handles the 401 bounce; the session rides the HttpOnly cookie.
// ---------------------------------------------------------------------------

export const supportPlatformClient = createTypedClient<SupportPlatformPaths>('');

// ---------------------------------------------------------------------------
// Identity Provider Registry (aaim-core authorization service, port 9551)
// Spec paths declare full /api/v1/identity-providers/* and
// /api/v1/tenant-identity-bindings/* prefixes. baseUrl is empty (default
// AUTHORIZATION_DEFAULT_BASE): platform-admin nginx proxies both prefixes
// straight to ${AAIM_CORE_AUTH_URL} (nginx.conf locations
// /api/v1/identity-providers + /api/v1/tenant-identity-bindings). Sprint
// rbac-03b (#1286): used by the 9303 IdentityProviderDetail "Tenant Bindings"
// tab (useTenantIdentityBindings.ts). authMiddleware() (client.ts) attaches
// the CSRF header on mutations and handles the 401 bounce; the session rides
// the HttpOnly cookie the backend shim translates.
// ---------------------------------------------------------------------------

export const identityProviderClient = createIdentityProviderClient();

// ---------------------------------------------------------------------------
// Admin Data Browser (platform-admin-api, port 9302) -- GH #1824
// Operator-scoped, IP-allowlisted, audited read-only data browser + the
// ephemeral paste-able-token mint for the AI/dev debug surface.
// Spec paths declare the full /api/v1/admin/data-browser/* prefix, so baseUrl
// is empty (same canonical pattern as supportPlatformClient / loanSyndicationClient).
// platform.aaim.com routes /api/* through the SPA nginx catch-all -> platform-admin-api.
// ---------------------------------------------------------------------------

export const dataBrowserClient = createTypedClient<DataBrowserPaths>('');

// ---------------------------------------------------------------------------
// Recovery cases (platform-admin-api forwarder to consumer-api) -- GH #4409
// Spec paths declare the full /api/v1/admin/recovery-cases prefix
// (consumer-anonymous-onboarding.openapi.yaml), so baseUrl is empty.
// platform-admin-api forwards them to consumer-api with the operator's bearer.
// In production they reach platform-admin-api only through a platform.aaim.com
// load-balancer rule for /api/v1/admin/recovery-cases and its /* form
// (gcp-infrastructure.yaml served_path_rules): with no rule the request falls
// to this SPA's default backend, whose nginx /api/ hop to the run.app host is
// refused by the service's ingress, and answers 404 HTML.
// ---------------------------------------------------------------------------

export const recoveryCaseAdminClient = createTypedClient<ConsumerAnonymousOnboardingPaths>('');
